 
import { BuildingComplex, Crown, Mail, Phone, User } from 'lucide-react';
import { ContactDetail } from 'bindings/contacts/ContactDetail';
import eye from 'images/icons/company/eye.svg';
import phone_button from 'images/icons/company/phone_button.svg';
import close from 'images/icons/orders/close.svg';
import { ITag } from 'proto/protobufs';
import * as React from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { match_email } from 'validators-web';
import { AContactEdition } from '../../../../atoms/contact/contactEdition';
import { AProfile, AUsers } from '../../../../atoms/global/users';
import ClientCompany from '../../../../components_v2/clientCompany/ClientCompany';
import DropdownOwners, { UserBlock } from '../../../../components_v2/dropdown/DropdownOwners';
import { DropdownSearchCompanies } from '../../../../components_v2/dropdown/DropdownSearch';
import TagsPicker from '../../../../components_v2/dropdown/TagsPicker';
import { blurOnEnterPressed } from '../../../../components_v2/input/Input';
import PageLoader from '../../../../components_v2/pageLoader/PageLoader';
import { PopupContext } from '../../../../components_v2/popup/Popup';
import { updateTags } from '../../../../components_v2/toolbarFilter/tags/action';
import { TagEditParams, TagType } from '../../../../components_v2/toolbarFilter/tags/Model';
import ToolTip from '../../../../styles/global/ToolTip';
import { Translate } from '../../../../styles/global/translate';
import { TitleAndChild } from '../../../client-companies/popup/Detail';
import { BodyMainContainer, BodyMainRow, BodyMainText, BodyMainTextEditable, ButtonAction } from '../../../client-companies/style/PopupStyle';
import { HoverEmailWrapper } from '../../../email-templates/emailTemplatePopup';
import { LoadingStateEnum } from '../../../import/model';
import Restricted from '../../../permissions/Restricted';
import { FlexDiv } from '../../../products/style';
import { QueryUpdateContact } from '../../data/Data';

function DetailInput(props: {
	imageJsx: JSX.Element,
	ownerId: number,
	fallback?: React.ReactNode,
	children: React.ReactNode
	translateId: string;
}) {
	return <BodyMainRow>
		<ToolTip toolTipContent={<Translate id={props.translateId} />} left='33%'>
			{props.imageJsx}
		</ToolTip>
		<Restricted
			to={{ objectAction: 'UpdateContact' }}
			ownerId={props.ownerId}
			fallback={props.fallback}
		>
			{props.children}
		</Restricted>
	</BodyMainRow>;
} 

export default function ContactPopupDetais(props: {
	contact: ContactDetail,
	updateContact: (contactId: number, params: QueryUpdateContact, localChanges?: { [key: string]: unknown } | undefined) => Promise<void | undefined>,
	tags: ITag[],
	readOnly: boolean
}) {
	const { isChild } = React.useContext(PopupContext);
	const { contact, updateContact } = props;
	const owners = useRecoilValue(AUsers);
	const profile = useRecoilValue(AProfile);
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADED);
	const ownersOption = React.useMemo(() => owners.map(owner => ({
		label: owner.name,
		value: owner,
		image: owner.photoUrl,
		selected: owner.isYou
	})), [owners]);
	const [isCompanyOpen, setIsCompanyOpen] = React.useState(false);
	const [tagLoadingState, setTagLoadingState] = React.useState(LoadingStateEnum.LOADED);
	const [, setAtomContactEdition] = useRecoilState(AContactEdition);
	const [dropdownWidth, setDropdownWidth] = React.useState<string>(`${window.innerWidth / 6}px`);

	//FIXME: I put this to calculate dynamically dropdown size because we have to give a size 'fixed' to our dropdown.
	// The performance will be not good as it renders everytime when resizing
	// Alternative: calculate dropdown size when only popup openned => with this method, no re-render but not dynamic
	React.useEffect(() => {
		const handleResize = () => {
			setDropdownWidth(`${window.innerWidth / 6}px`);
		};

		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);


	const selectedUser = owners.find(o => o.id === contact.owner_id);

	function updateContactTags(params: TagEditParams): void {
		setTagLoadingState(LoadingStateEnum.LOADING);
		updateTags(TagType.CONTACT, params)
			.then(response => {
				setTagLoadingState(LoadingStateEnum.LOADED);
				if (response.valueOf()) setAtomContactEdition({ id: contact.id, ...params });
			})
			.catch(() => setTagLoadingState(LoadingStateEnum.LOADED));
	}

	return <FlexDiv flow="column" width='100%' padding='15px 0 0 0'>
		<TagsPicker
			readOnly={props.readOnly}
			paddingLeft='10px'
			loading={tagLoadingState === LoadingStateEnum.LOADING}
			tags={props.tags}
			selected={props.tags.filter(t => contact.tags?.includes(t.id!))}
			onAdd={tag => updateContactTags({ ressource_id: contact.id, ids_add: [tag.id!] })}
			onDelete={id => updateContactTags({ ressource_id: contact.id, ids_delete: [id] })}
		/>
		<TitleAndChild title={<Translate id='overview' />} defaultOpen>
			<BodyMainContainer noScroll noHeight>
				<DetailInput imageJsx={<User/>} fallback={<UserBlock user={selectedUser}/>} ownerId={profile.id} translateId='owner'>
					<DropdownOwners
						disabled={props.readOnly}
						dropdownStyle={{ margin:'0 0 0 20px', optionWidth: '200px', optionHeight: '150px', width: '230px' }}
						users={ownersOption}
						selected={ownersOption.find(o => o.value.id === selectedUser?.id)}
						onChange={v => updateContact(contact.id, { owner_id: v.value.id })}
						open
					/>
				</DetailInput>
				<DetailInput imageJsx={<Crown/>} fallback={<BodyMainText display='block'>{contact.position}</BodyMainText>} ownerId={profile.id} translateId='company.creation.contact.position'>
					<BodyMainTextEditable
						marginLeft='20px'
						suppressContentEditableWarning
						contentEditable={!props.readOnly}
						onKeyDownCapture={blurOnEnterPressed}
						onBlur={e => {
							if (props.readOnly) return;
							const newPosition = e.currentTarget.innerText.trim();
							updateContact(contact.id, { position: newPosition ? { updated: newPosition } : 'deleted' });
						}}
					>
						{contact.position}
					</BodyMainTextEditable>
				</DetailInput>
				<DetailInput imageJsx={<Phone/>} fallback={<BodyMainText display='block'>{contact.phone}</BodyMainText>} ownerId={profile.id} translateId='phone'>
					<BodyMainTextEditable
						marginLeft='20px'
						suppressContentEditableWarning
						contentEditable={!props.readOnly}
						onKeyDownCapture={blurOnEnterPressed}
						onBlur={e => {
							if (props.readOnly) return;
							const newPhone = e.currentTarget.innerText.trim();
							updateContact(contact.id, { phone: newPhone ? { updated: newPhone } : 'deleted' });
						}}
					>
						{contact.phone}
					</BodyMainTextEditable>
					{contact.phone && <ButtonAction src={phone_button} alt="phone" onClick={() => window.open(`tel:${contact.phone}`)} />}
				</DetailInput>
				<DetailInput imageJsx={<Mail/>} fallback={<BodyMainText display='block'>{contact.email}</BodyMainText>} ownerId={profile.id} translateId='email'>
					<BodyMainTextEditable
						marginLeft='20px'
						suppressContentEditableWarning
						contentEditable={!props.readOnly}
						minWidth='160px'
						onKeyDownCapture={blurOnEnterPressed}
						onBlur={e => {
							if (props.readOnly) return;
							const newEmail = e.currentTarget.innerText.trim();
							if (!newEmail) return updateContact(contact.id, { email: 'deleted' });
							if (match_email(newEmail)) updateContact(contact.id, { email: { updated: newEmail } });
						}}
					>
						{contact.email}
					</BodyMainTextEditable>
					{contact.email && 
						<HoverEmailWrapper
							emailTemplateVariable={{
								first_name:contact.first_name ?? '',
								last_name:contact.last_name ?? '',
								company_name:contact.client_company_name ?? '',
							}}
							email={contact.email} />
					}
				</DetailInput>
				<DetailInput imageJsx={<BuildingComplex/>} fallback={<BodyMainText display='block'>{contact.client_company_id}</BodyMainText>} ownerId={profile.id} translateId='company.columns.company_address'>
					<>
						<BodyMainText noOverflow marginLeft='20px'>
							<DropdownSearchCompanies
								disabled={props.readOnly}
								dropdownStyle={{ optionWidth: dropdownWidth, optionHeight: '200px', containerWidth: '100%', height: '25px', width: '100%' }}
								JSXButton={(handleChange) => <>
									{loadingState == LoadingStateEnum.LOADING && <FlexDiv height="100%"><PageLoader size={20} /></FlexDiv>}
									{loadingState !== LoadingStateEnum.LOADING &&
										<BodyMainTextEditable
											marginLeft="0px"
											suppressContentEditableWarning
											contentEditable
											onKeyDownCapture={blurOnEnterPressed}
											onInput={(e) => handleChange(e.currentTarget.innerText.trim())}
											display='block'
										>
											{contact.client_company_name}
										</BodyMainTextEditable>}
								</>
								}
								name={''}
								onChange={value => {
									setLoadingState(LoadingStateEnum.LOADING);
									updateContact(contact.id, { client_company_id: value ? { updated: value.value.id } : 'deleted' }, { client_company_name: value?.value.name })
										.then(() => setLoadingState(LoadingStateEnum.LOADED))
										.catch(() => setLoadingState(LoadingStateEnum.ERROR));
								}}
							/>
						</BodyMainText>
						{!props.readOnly && contact.client_company_name && <ButtonAction
							src={close}
							onClick={() => {
								setLoadingState(LoadingStateEnum.LOADING);
								updateContact(contact.id, { client_company_id: 'deleted' }, { client_company_name: 'deleted' })
									.then(() => setLoadingState(LoadingStateEnum.LOADED))
									.catch(() => setLoadingState(LoadingStateEnum.ERROR));
							}}
						/>}
						{contact.client_company_name && <ButtonAction src={eye} onClick={() => isChild ? window.open(`companies?id=${contact.client_company_id}`) : setIsCompanyOpen(true)} />}
					</>
					{contact.client_company_id &&
						<ClientCompany
							isOpen={isCompanyOpen}
							setOpen={setIsCompanyOpen}
							clientCompanyId={contact.client_company_id}
						/>
					}
				</DetailInput>
			</BodyMainContainer>
		</TitleAndChild>
	</FlexDiv>;
}